import { useState } from "react";
import { BrandName } from "@/shared/branding";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Mountain, Lock, KeyRound, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { resetPasswordByToken } from "@/services/auth";
import { ApiError } from "@/services/api";

/**
 * PWRESET-1 · 忘记密码 · 邮件链接落地页（/reset-password?token=…）
 *
 * 令牌只从 URL 读，不落任何 store；输两遍新密码 → POST /auth/reset-password。
 * 400 = 链接无效/过期（给「重新申请」入口）· 422 = 强度不足（原样显示后端的原因）。
 * 成功后不自动登录（后端只改密码，不签令牌）——回登录页用新密码登，MFA 照常。
 */
// 外壳放模块级：写在组件函数体内每次渲染都是新类型 → 输入框每敲一键就被卸载重挂、失焦。
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600">
            <Mountain className="h-5 w-5 text-white" strokeWidth={2} />
          </div>
          <div className="flex flex-col">
            <span className="text-lg font-bold text-slate-900"><BrandName /></span>
            <span className="text-[12px] text-slate-500">运营后台 · v1</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw1 !== pw2) {
      setError("两次输入的密码不一致");
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordByToken(token, pw1);
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setExpired(true);
      } else if (err instanceof ApiError) {
        setError(err.message || "重置失败");
      } else {
        setError("网络错误 · 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || expired) {
    return (
      <Shell>
        <div className="mb-1 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-rose-600" />
          <h1 className="text-xl font-bold text-slate-900">重置链接无效或已过期</h1>
        </div>
        <p className="mb-6 text-[13px] text-slate-500">
          链接 30 分钟内有效且只能用一次。请回到忘记密码页重新申请一封。
        </p>
        <Link
          to="/forgot-password"
          className="flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700"
        >
          重新申请 <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="mb-1 flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          <h1 className="text-xl font-bold text-slate-900">密码已重置</h1>
        </div>
        <p className="mb-6 text-[13px] text-slate-500" data-testid="reset-done">
          请用新密码登录。启用了双重验证的账号照常输入验证码。
        </p>
        <button
          type="button"
          onClick={() => navigate("/login", { replace: true })}
          className="flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700"
        >
          去登录 <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-1 flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-blue-600" />
        <h1 className="text-xl font-bold text-slate-900">设置新密码</h1>
      </div>
      <p className="mb-6 text-[13px] text-slate-500">至少 8 位，须同时包含大写字母、小写字母和数字</p>

      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-slate-700">新密码</span>
          <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
            <Lock className="h-4 w-4 text-slate-400" />
            <input
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              className="flex-1 bg-transparent text-[13px] outline-none"
              autoComplete="new-password"
              autoFocus
              required
            />
          </div>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[12px] font-medium text-slate-700">再输一遍</span>
          <div className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
            <Lock className="h-4 w-4 text-slate-400" />
            <input
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="flex-1 bg-transparent text-[13px] outline-none"
              autoComplete="new-password"
              required
            />
          </div>
        </label>

        {error && <div className="text-[12px] font-medium text-rose-600">{error}</div>}

        <button
          type="submit"
          disabled={submitting}
          data-testid="reset-submit"
          className="mt-1 flex h-10 items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              确认重置 <ArrowRight className="h-3.5 w-3.5" />
            </>
          )}
        </button>
      </form>
    </Shell>
  );
}
